import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { isFocusable } from "../../../models/utilities/defaultFocusStatusUtilities";
import { KeyboardNavigation } from "./KeyboardNavigation";

/**
 * CarrouselNavigation:
 * A specialized class handling keyboard focus for:
 *  - "Previous" arrow button
 *  - "Next" arrow button
 *  - Choice items in the carousel question
 */
export class CarrouselNavigation extends KeyboardNavigation {
    private getElementId(element: DefaultFocusStatusModel): string {
        return typeof element?.elementId === "string" ? element.elementId : "";
    }

    private isCarouselArrow(element: DefaultFocusStatusModel): boolean {
        const elementId = this.getElementId(element);
        return elementId.includes("CarrouselPrevArrow") || elementId.includes("CarrouselNextArrow");
    }

    private isCarouselStatement(element: DefaultFocusStatusModel): boolean {
        return this.getElementId(element).includes("statement_");
    }

    private isChoiceElement(element: DefaultFocusStatusModel): boolean {
        const elementId = this.getElementId(element);
        return elementId.includes("_INPUT") || elementId.includes("_C_");
    }

    private isSameQuestion(
        source: DefaultFocusStatusModel,
        target: DefaultFocusStatusModel,
    ): boolean {
        if (!source || !target) return false;
        if (!source.questionId || !target.questionId) return false;
        return source.questionId === target.questionId;
    }

    private findNextFocusableChoiceWithinQuestion(
        currentElement: DefaultFocusStatusModel,
        startIndex: number,
    ): DefaultFocusStatusModel | null {
        for (let i = startIndex, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const candidate = this.defaultFocusStatus.statuses[i];
            if (
                this.isSameQuestion(currentElement, candidate) &&
                this.isChoiceElement(candidate) &&
                candidate.elementRef?.current &&
                isFocusable(candidate)
            ) {
                return candidate;
            }
        }
        return null;
    }

    private isCarouselNavigationElement(element: DefaultFocusStatusModel): boolean {
        return (
            this.isCarouselArrow(element) ||
            this.isCarouselStatement(element) ||
            this.isChoiceElement(element)
        );
    }

    /**
     * Moves focus among elements within the same variable/question
     * when user presses arrow keys (Left/Right).
     */
    findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        return null;
    }

    /**
     * When user presses TAB, we jump to the next variable's first focusable element.
     */
    findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const currentIndex = this.defaultFocusStatus.statuses.indexOf(currentElement);

        // From a statement, move to a choice in the same question first.
        if (this.isCarouselStatement(currentElement) && currentIndex >= 0) {
            const forwardChoice = this.findNextFocusableChoiceWithinQuestion(
                currentElement,
                currentIndex + 1,
            );
            if (forwardChoice) return forwardChoice;

            const fallbackChoice = this.findNextFocusableChoiceWithinQuestion(currentElement, 0);
            if (fallbackChoice) return fallbackChoice;
        }

        // Default behavior for other elements
        return this.findNextFocusableElementWithinNextVariableCommon(currentElement);
    }

    /**
     * When user presses SHIFT+TAB, we jump to the previous variable's last focusable element.
     */
    findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        return this.findLastFocusableElementWithinPreviousVariableCommon(currentElement);
    }

    // Override the common method to ensure visibility checks
    findNextFocusableElementWithinNextVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        // Check if current element is a carousel navigation arrow
        const isFromCarouselArrow =
            (typeof currentElement.elementId === "string" &&
                (currentElement.elementId.includes("CarrouselPrevArrow") ||
                    currentElement.elementId.includes("CarrouselNextArrow"))) ||
            false;

        const shouldPreferSameQuestion =
            this.isCarouselNavigationElement(currentElement) &&
            currentElement.questionId !== undefined;

        if (shouldPreferSameQuestion) {
            // From carousel arrows, prioritize the question's available choices.
            if (this.isCarouselArrow(currentElement)) {
                const forwardChoice = this.findNextFocusableChoiceWithinQuestion(
                    currentElement,
                    index + 1,
                );
                if (forwardChoice) return forwardChoice;
            }

            for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
                const sameQuestionCandidate = this.defaultFocusStatus.statuses[i];
                if (
                    this.isSameQuestion(currentElement, sameQuestionCandidate) &&
                    sameQuestionCandidate.elementRef?.current &&
                    isFocusable(sameQuestionCandidate)
                ) {
                    return sameQuestionCandidate;
                }
            }
        }

        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;

            // For carousel arrows, we need special handling when the target would be another arrow
            if (isFromCarouselArrow) {
                const isTargetCarouselArrow =
                    (typeof elementFocusStatus.elementId === "string" &&
                        (elementFocusStatus.elementId.includes("CarrouselPrevArrow") ||
                            elementFocusStatus.elementId.includes("CarrouselNextArrow"))) ||
                    false;

                // If the target is another carousel arrow, check if it's focusable
                if (isTargetCarouselArrow && !isFocusable(elementFocusStatus)) {
                    // If not focusable, continue to the next element after the arrow
                    continue;
                }
            }

            if (
                elementFocusStatus.elementRef?.current &&
                ((elementFocusStatus.variableId !== 0 && variableCondition) ||
                    (elementFocusStatus.variableId == currentElement.variableId &&
                        elementFocusStatus.elementRef?.current?.tagName == "TEXTAREA"))
            ) {
                return this.getFocusableElement(elementFocusStatus, true);
            }
        }
        return null;
    }

    findLastFocusableElementWithinPreviousVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        const shouldPreferSameQuestion =
            this.isCarouselNavigationElement(currentElement) &&
            currentElement.questionId !== undefined;

        if (shouldPreferSameQuestion) {
            for (let i = index - 1; i >= 0; i--) {
                const sameQuestionCandidate = this.defaultFocusStatus.statuses[i];
                if (
                    this.isSameQuestion(currentElement, sameQuestionCandidate) &&
                    sameQuestionCandidate.elementRef?.current &&
                    isFocusable(sameQuestionCandidate)
                ) {
                    return sameQuestionCandidate;
                }
            }
        }

        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;

            const isTargetCarouselArrow =
                (typeof elementFocusStatus.elementId === "string" &&
                    (elementFocusStatus.elementId.includes("CarrouselPrevArrow") ||
                        elementFocusStatus.elementId.includes("CarrouselNextArrow"))) ||
                false;

            // If the target is another carousel arrow, check if it's focusable
            if (isTargetCarouselArrow && !isFocusable(elementFocusStatus)) {
                // If not focusable, continue to the next element after the arrow
                continue;
            }

            if (
                elementFocusStatus.elementRef?.current &&
                elementFocusStatus.variableId !== 0 &&
                variableCondition
            ) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }
        return null;
    }
}
